// Staff-created court reservation (#2269).
//
// This is a thin operator form over the existing Facility contracts. It finds
// one facility-known player by resolved name, converts facility-local wall time,
// renders non-binding server availability and pricing, and submits the exact
// reviewed inputs. Payment is online only: staff book it free or send the
// player a pay link. The backend remains authoritative at commit time; this
// component never computes money or optimistically inserts a booking.

import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useForm } from "react-hook-form";
import { isApiError } from "@bw/shared/api";
import { asMoney } from "@bw/shared/types";
import { formatMoney } from "@bw/shared/utils";
import { z } from "zod";
import type { StaffBookingPayment } from "@bw/shared/calendar/contract";

import {
  createStaffBooking,
  getStaffBookingQuote,
  searchFacilityPlayers,
  type FacilityPlayerSearchResult,
  type StaffBookingBody,
  type StaffBookingQuoteBody,
} from "@/libs/bookingsApi";
import {
  previewCourtAvailability,
  type CourtAvailabilityCourtVerdict,
} from "@/libs/courtAvailabilityApi";
import type { Court, PlayerAllocationConfig } from "@/libs/facilityApi";
import { fromZonedDateTimeInput } from "@/libs/zonedDateTime";

type RequestStatus = "idle" | "loading" | "ready" | "error";

interface CreateBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facilityId: string;
  facilityName: string;
  facilityTimeZone: string;
  playerAllocation: PlayerAllocationConfig | null;
  courts: Court[];
  onCreated: () => void;
  returnFocusRef: RefObject<HTMLButtonElement | null>;
  /**
   * Whether "Free" is offered for a priced booking: owner, manager and front
   * desk only (`hasStaffScheduleRole`, mirroring the backend's role rule).
   * Fails closed to pay link only; the server re-checks.
   */
  canWaivePayment?: boolean;
  /**
   * Prefill from a calendar slot: facility-local `YYYY-MM-DDTHH:mm` values and
   * an optional court. Applied each time the dialog opens.
   */
  initialSlot?: {
    startLocal: string;
    endLocal: string;
    courtId?: string;
  } | null;
}

interface ValidInterval {
  startTime: string;
  endTime: string;
  durationMinutes: number;
  key: string;
}

interface ValidCounts {
  playerCount: number;
  nonMemberPlayerCount: number;
  key: string;
}

interface AvailabilityInput {
  key: string;
  courtIds: string[];
  interval: ValidInterval;
}

interface QuoteInput {
  key: string;
  body: StaffBookingQuoteBody;
}

interface BookingFormValues {
  customerQuery: string;
  startLocal: string;
  endLocal: string;
  playerCountInput: string;
  nonMemberCountInput: string;
  selectedCourtId: string;
}

const EMPTY_BOOKING_FORM: BookingFormValues = {
  customerQuery: "",
  startLocal: "",
  endLocal: "",
  playerCountInput: "1",
  nonMemberCountInput: "0",
  selectedCourtId: "",
};

const PLAYER_SEARCH_DELAY_MS = 200;
const AVAILABILITY_DELAY_MS = 150;
const QUOTE_DELAY_MS = 100;

function parseWholeNumber(value: string): number | null {
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function resolveAllocationCap(
  facilityCap: number | null | undefined,
  courtCap: number | null | undefined,
): number {
  const caps = [facilityCap, courtCap].filter(
    (value): value is number =>
      typeof value === "number" && Number.isInteger(value) && value > 0,
  );
  return caps.length > 0 ? Math.min(...caps) : 1;
}

function createBookingFormSchema(
  facilityTimeZone: string,
  allocationEnabled: boolean,
  facilityCap: number | null | undefined,
  enabledCourts: Court[],
) {
  return z
    .object({
      customerQuery: z.string(),
      startLocal: z.string(),
      endLocal: z.string(),
      playerCountInput: z.string(),
      nonMemberCountInput: z.string(),
      selectedCourtId: z.string(),
    })
    .superRefine((values, context) => {
      if (values.startLocal || values.endLocal) {
        if (!values.startLocal || !values.endLocal) {
          context.addIssue({
            code: "custom",
            message: "Enter both a start time and an end time.",
            path: [values.startLocal ? "endLocal" : "startLocal"],
          });
        } else {
          const startTime = fromZonedDateTimeInput(
            values.startLocal,
            facilityTimeZone,
          );
          const endTime = fromZonedDateTimeInput(
            values.endLocal,
            facilityTimeZone,
          );
          if (!startTime || !endTime) {
            context.addIssue({
              code: "custom",
              message: `Enter valid times in ${facilityTimeZone}.`,
              path: [!startTime ? "startLocal" : "endLocal"],
            });
          } else {
            const durationMinutes =
              (Date.parse(endTime) - Date.parse(startTime)) / 60_000;
            if (durationMinutes <= 0) {
              context.addIssue({
                code: "custom",
                message: "End time must be after the start time.",
                path: ["endLocal"],
              });
            } else if (!Number.isInteger(durationMinutes)) {
              context.addIssue({
                code: "custom",
                message: "Reservation duration must use whole minutes.",
                path: ["endLocal"],
              });
            }
          }
        }
      }

      if (!allocationEnabled) return;
      const playerCount = parseWholeNumber(values.playerCountInput);
      const nonMemberPlayerCount = parseWholeNumber(values.nonMemberCountInput);
      if (playerCount === null || playerCount < 1) {
        context.addIssue({
          code: "custom",
          message: "Party size must be at least 1.",
          path: ["playerCountInput"],
        });
        return;
      }
      if (nonMemberPlayerCount === null) {
        context.addIssue({
          code: "custom",
          message: "Non-member players must be a whole number of 0 or more.",
          path: ["nonMemberCountInput"],
        });
        return;
      }
      if (nonMemberPlayerCount > playerCount) {
        context.addIssue({
          code: "custom",
          message: "Non-member players cannot exceed the party size.",
          path: ["nonMemberCountInput"],
        });
      }
      const selectedCourt = enabledCourts.find(
        (court) => court.court_id === values.selectedCourtId,
      );
      const cap = resolveAllocationCap(facilityCap, selectedCourt?.max_players);
      if (playerCount > cap) {
        context.addIssue({
          code: "custom",
          message: `Party size cannot exceed the maximum of ${cap} for the selected court.`,
          path: ["playerCountInput"],
        });
      }
    });
}

function availabilityText(
  status: RequestStatus,
  verdict: CourtAvailabilityCourtVerdict | undefined,
): string {
  if (status === "loading") return "Checking availability";
  if (status === "error") return "Availability could not be verified";
  if (status !== "ready" || !verdict)
    return "Enter times to check availability";
  if (verdict.available) return "Available";
  if (verdict.unavailable_reason === "court_disabled")
    return "Unavailable: court disabled";
  if (verdict.unavailable_reason === "outside_operating_hours") {
    return "Unavailable: outside operating hours";
  }
  return "Unavailable: another reservation uses this time";
}

export function CreateBookingDialog({
  open,
  onOpenChange,
  facilityId,
  facilityName,
  facilityTimeZone,
  playerAllocation,
  courts,
  onCreated,
  returnFocusRef,
  initialSlot = null,
  canWaivePayment = false,
}: CreateBookingDialogProps) {
  const enabledCourts = useMemo(
    () => courts.filter((court) => court.status === "enabled"),
    [courts],
  );
  const enabledCourtIdsKey = useMemo(
    () => enabledCourts.map((court) => court.court_id).join(","),
    [enabledCourts],
  );
  const allocationEnabled = playerAllocation?.enabled === true;
  const formSchema = useMemo(
    () =>
      createBookingFormSchema(
        facilityTimeZone,
        allocationEnabled,
        playerAllocation?.max_count,
        enabledCourts,
      ),
    [
      allocationEnabled,
      enabledCourts,
      facilityTimeZone,
      playerAllocation?.max_count,
    ],
  );
  const form = useForm<BookingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY_BOOKING_FORM,
    mode: "onChange",
  });
  const customerQuery = form.watch("customerQuery");
  const startLocal = form.watch("startLocal");
  const endLocal = form.watch("endLocal");
  const playerCountInput = form.watch("playerCountInput");
  const nonMemberCountInput = form.watch("nonMemberCountInput");
  const selectedCourtIdValue = form.watch("selectedCourtId");
  const selectedCourtId = selectedCourtIdValue || null;
  const [settledCustomerQuery, setSettledCustomerQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] =
    useState<FacilityPlayerSearchResult | null>(null);
  const [debouncedAvailabilityKey, setDebouncedAvailabilityKey] = useState<
    string | null
  >(null);
  const [debouncedQuoteKey, setDebouncedQuoteKey] = useState<string | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [payment, setPayment] = useState<StaffBookingPayment>("pay_link");
  const [availabilityRefreshRevision, setAvailabilityRefreshRevision] =
    useState(0);
  const submitInFlight = useRef(false);

  const initialStart = initialSlot?.startLocal ?? "";
  const initialEnd = initialSlot?.endLocal ?? "";
  const initialCourtId = initialSlot?.courtId ?? "";
  const reset = useCallback(() => {
    form.reset({
      ...EMPTY_BOOKING_FORM,
      startLocal: initialStart,
      endLocal: initialEnd,
      selectedCourtId: enabledCourts.some(
        (court) => court.court_id === initialCourtId,
      )
        ? initialCourtId
        : "",
    });
    setSettledCustomerQuery("");
    setSelectedCustomer(null);
    setDebouncedAvailabilityKey(null);
    setDebouncedQuoteKey(null);
    setSubmitError(null);
    setSuccessMessage(null);
    setSubmitting(false);
    setPayment("pay_link");
    setAvailabilityRefreshRevision(0);
    submitInFlight.current = false;
  }, [enabledCourts, form, initialCourtId, initialEnd, initialStart]);

  // Reset (and apply any slot prefill) only when the dialog opens or the
  // facility changes — never mid-edit because a court list refetched.
  const resetRef = useRef(reset);
  resetRef.current = reset;
  useEffect(() => {
    if (open) resetRef.current();
  }, [facilityId, open]);

  const selectedCourt = useMemo(
    () =>
      selectedCourtId
        ? (enabledCourts.find((court) => court.court_id === selectedCourtId) ??
          null)
        : null,
    [enabledCourts, selectedCourtId],
  );
  const allocationCap = allocationEnabled
    ? resolveAllocationCap(
        playerAllocation?.max_count,
        selectedCourt?.max_players,
      )
    : 1;

  const formValidation = useMemo(
    () =>
      formSchema.safeParse({
        customerQuery,
        startLocal,
        endLocal,
        playerCountInput,
        nonMemberCountInput,
        selectedCourtId: selectedCourtIdValue,
      }),
    [
      customerQuery,
      endLocal,
      formSchema,
      nonMemberCountInput,
      playerCountInput,
      selectedCourtIdValue,
      startLocal,
    ],
  );
  const validationIssues = formValidation.success
    ? []
    : formValidation.error.issues;
  const startValidationIssue =
    validationIssues.find((issue) => issue.path[0] === "startLocal") ?? null;
  const endValidationIssue =
    validationIssues.find((issue) => issue.path[0] === "endLocal") ?? null;
  const playerCountValidationIssue =
    validationIssues.find((issue) => issue.path[0] === "playerCountInput") ??
    null;
  const nonMemberCountValidationIssue =
    validationIssues.find((issue) => issue.path[0] === "nonMemberCountInput") ??
    null;
  const intervalValidationIssue = startValidationIssue ?? endValidationIssue;
  const countsValidationIssue =
    playerCountValidationIssue ?? nonMemberCountValidationIssue;

  const intervalResult = useMemo<{
    value: ValidInterval | null;
  }>(() => {
    if (!startLocal && !endLocal) return { value: null };
    if (intervalValidationIssue) return { value: null };
    const startTime = fromZonedDateTimeInput(startLocal, facilityTimeZone);
    const endTime = fromZonedDateTimeInput(endLocal, facilityTimeZone);
    if (!startTime || !endTime) return { value: null };
    const durationMinutes =
      (Date.parse(endTime) - Date.parse(startTime)) / 60_000;
    return {
      value: {
        startTime,
        endTime,
        durationMinutes,
        key: JSON.stringify([startTime, endTime, durationMinutes]),
      },
    };
  }, [endLocal, facilityTimeZone, intervalValidationIssue, startLocal]);

  const countsResult = useMemo<{
    value: ValidCounts | null;
  }>(() => {
    if (!allocationEnabled) {
      return {
        value: {
          playerCount: 1,
          nonMemberPlayerCount: 0,
          key: "1:0",
        },
      };
    }
    if (countsValidationIssue) return { value: null };
    const playerCount = parseWholeNumber(playerCountInput);
    const nonMemberPlayerCount = parseWholeNumber(nonMemberCountInput);
    if (playerCount === null || nonMemberPlayerCount === null)
      return { value: null };
    return {
      value: {
        playerCount,
        nonMemberPlayerCount,
        key: `${playerCount}:${nonMemberPlayerCount}`,
      },
    };
  }, [
    allocationEnabled,
    countsValidationIssue,
    nonMemberCountInput,
    playerCountInput,
  ]);

  // Debounce only the UI input. React Query owns the server response lifecycle,
  // drops superseded keys, and immediately hides data for a different query.
  const normalizedCustomerQuery = customerQuery.trim();
  useEffect(() => {
    if (!open || selectedCustomer || normalizedCustomerQuery.length < 2) {
      setSettledCustomerQuery("");
      return;
    }
    const timeout = window.setTimeout(
      () => setSettledCustomerQuery(normalizedCustomerQuery),
      PLAYER_SEARCH_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [normalizedCustomerQuery, open, selectedCustomer]);

  const customerSearchEnabled =
    open &&
    !selectedCustomer &&
    normalizedCustomerQuery.length >= 2 &&
    settledCustomerQuery === normalizedCustomerQuery;
  const customerSearchQuery = useQuery({
    queryKey: [
      "staff-booking-customer-search",
      facilityId,
      settledCustomerQuery,
    ],
    queryFn: async () => {
      const results = await searchFacilityPlayers(
        facilityId,
        settledCustomerQuery,
      );
      return results.filter(
        (result) =>
          Boolean(result.display_name) &&
          result.display_name !== result.user_id,
      );
    },
    enabled: customerSearchEnabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const customerSearchStatus: RequestStatus =
    !open || selectedCustomer || normalizedCustomerQuery.length < 2
      ? "idle"
      : !customerSearchEnabled ||
          customerSearchQuery.isPending ||
          customerSearchQuery.isFetching
        ? "loading"
        : customerSearchQuery.isError
          ? "error"
          : customerSearchQuery.isSuccess
            ? "ready"
            : "error";
  const customerResults =
    customerSearchStatus === "ready" ? (customerSearchQuery.data ?? []) : [];

  // A changed interval or explicit post-conflict revision creates a distinct
  // query key. Until that exact key resolves, every court fails closed.
  const availabilityInput = useMemo<AvailabilityInput | null>(() => {
    if (!open || !intervalResult.value || enabledCourts.length === 0)
      return null;
    const courtIds = enabledCourts.map((court) => court.court_id);
    return {
      key: JSON.stringify([
        facilityId,
        facilityTimeZone,
        intervalResult.value.key,
        enabledCourtIdsKey,
        availabilityRefreshRevision,
      ]),
      courtIds,
      interval: intervalResult.value,
    };
  }, [
    availabilityRefreshRevision,
    enabledCourtIdsKey,
    enabledCourts,
    facilityId,
    facilityTimeZone,
    intervalResult.value,
    open,
  ]);

  useEffect(() => {
    if (!intervalResult.value && selectedCourtIdValue) {
      form.setValue("selectedCourtId", "", { shouldValidate: true });
    }
  }, [form, intervalResult.value, selectedCourtIdValue]);

  useEffect(() => {
    if (!availabilityInput) {
      setDebouncedAvailabilityKey(null);
      return;
    }
    const timeout = window.setTimeout(
      () => setDebouncedAvailabilityKey(availabilityInput.key),
      AVAILABILITY_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [availabilityInput]);

  const availabilityQueryEnabled =
    availabilityInput !== null &&
    debouncedAvailabilityKey === availabilityInput.key;
  const availabilityQuery = useQuery({
    queryKey: [
      "staff-booking-court-availability",
      facilityId,
      debouncedAvailabilityKey,
    ],
    queryFn: async () => {
      if (
        !availabilityInput ||
        availabilityInput.key !== debouncedAvailabilityKey
      ) {
        throw new Error("Court availability request was superseded.");
      }
      const result = await previewCourtAvailability(facilityId, {
        court_ids: availabilityInput.courtIds,
        start_time: availabilityInput.interval.startTime,
        end_time: availabilityInput.interval.endTime,
        minimum_duration_minutes: availabilityInput.interval.durationMinutes,
      });
      // The API client already decoded the exact facility, interval, duration,
      // and court set. Bind that result to the timezone snapshot used for these
      // wall-time inputs so a facility setting change also fails closed.
      if (result.timezone !== facilityTimeZone) {
        throw new Error("Facility timezone changed; refresh and try again.");
      }
      return result;
    },
    enabled: availabilityQueryEnabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const availabilityStatus: RequestStatus = !availabilityInput
    ? "idle"
    : !availabilityQueryEnabled ||
        availabilityQuery.isPending ||
        availabilityQuery.isFetching
      ? "loading"
      : availabilityQuery.isError
        ? "error"
        : availabilityQuery.isSuccess
          ? "ready"
          : "error";
  const availability =
    availabilityStatus === "ready" ? availabilityQuery.data : null;
  const verdicts = useMemo(
    () =>
      new Map(
        availability
          ? availability.courts.map((court) => [court.court_id, court])
          : [],
      ),
    [availability],
  );
  const selectedVerdict = selectedCourtId
    ? verdicts.get(selectedCourtId)
    : undefined;

  // Quote only the exact currently available court and exact allocation. The
  // availability revision binds a post-conflict quote to the fresh preview.
  const quoteInput = useMemo<QuoteInput | null>(() => {
    if (
      !open ||
      !selectedCourtId ||
      !intervalResult.value ||
      !countsResult.value ||
      availabilityStatus !== "ready" ||
      selectedVerdict?.available !== true
    ) {
      return null;
    }
    const body: StaffBookingQuoteBody = {
      court_id: selectedCourtId,
      start_time: intervalResult.value.startTime,
      end_time: intervalResult.value.endTime,
      timezone: facilityTimeZone,
      player_count: countsResult.value.playerCount,
      non_member_player_count: countsResult.value.nonMemberPlayerCount,
    };
    return {
      key: JSON.stringify([
        facilityId,
        selectedCourtId,
        intervalResult.value.key,
        countsResult.value.key,
        facilityTimeZone,
        availabilityRefreshRevision,
      ]),
      body,
    };
  }, [
    availabilityRefreshRevision,
    availabilityStatus,
    countsResult.value,
    facilityId,
    facilityTimeZone,
    intervalResult.value,
    open,
    selectedCourtId,
    selectedVerdict?.available,
  ]);

  useEffect(() => {
    if (!quoteInput) {
      setDebouncedQuoteKey(null);
      return;
    }
    const timeout = window.setTimeout(
      () => setDebouncedQuoteKey(quoteInput.key),
      QUOTE_DELAY_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [quoteInput]);

  const quoteQueryEnabled =
    quoteInput !== null && debouncedQuoteKey === quoteInput.key;
  const quoteQuery = useQuery({
    queryKey: ["staff-booking-quote", facilityId, debouncedQuoteKey],
    queryFn: async () => {
      if (!quoteInput || quoteInput.key !== debouncedQuoteKey) {
        throw new Error("Staff booking quote request was superseded.");
      }
      const result = await getStaffBookingQuote(facilityId, quoteInput.body);
      if (
        result.facility_id !== facilityId ||
        result.player_count !== quoteInput.body.player_count ||
        result.non_member_player_count !==
          quoteInput.body.non_member_player_count
      ) {
        throw new Error("Staff booking quote did not match the request.");
      }
      return result;
    },
    enabled: quoteQueryEnabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const quoteStatus: RequestStatus = !quoteInput
    ? "idle"
    : !quoteQueryEnabled || quoteQuery.isPending || quoteQuery.isFetching
      ? "loading"
      : quoteQuery.isError
        ? "error"
        : quoteQuery.isSuccess
          ? "ready"
          : "error";
  const quote = quoteStatus === "ready" ? quoteQuery.data : null;
  // With no matching rate the server prices the booking at $0, so there is
  // nothing for the player to pay and no payment choice to offer.
  const noCharge = quote?.pricing_rule_matched === false;

  const createBookingMutation = useMutation({
    mutationFn: (body: StaffBookingBody) =>
      createStaffBooking(facilityId, body),
    retry: false,
  });

  const visibleError =
    submitError ??
    (availabilityStatus === "error"
      ? "Court availability could not be verified. Check the times and try again."
      : quoteStatus === "error"
        ? "The reservation price could not be loaded. Try again."
        : null);

  const canSubmit = Boolean(
    selectedCustomer &&
      selectedCourtId &&
      intervalResult.value &&
      countsResult.value &&
      availabilityStatus === "ready" &&
      selectedVerdict?.available === true &&
      quoteStatus === "ready" &&
      quote &&
      !submitting,
  );

  const handleSubmit = async () => {
    if (submitInFlight.current || !canSubmit) return;
    const customer = selectedCustomer;
    const courtId = selectedCourtId;
    const interval = intervalResult.value;
    const counts = countsResult.value;
    if (!customer || !courtId || !interval || !counts) return;

    submitInFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await createBookingMutation.mutateAsync({
        court_id: courtId,
        player_user_id: customer.user_id,
        start_time: interval.startTime,
        end_time: interval.endTime,
        player_count: counts.playerCount,
        non_member_player_count: counts.nonMemberPlayerCount,
        // No matching rate means nothing to collect: it is booked free.
        payment: noCharge ? "free" : canWaivePayment ? payment : "pay_link",
      });
      setSuccessMessage(
        created.payment === "free"
          ? `Reservation created for ${customer.display_name} at no charge.`
          : `Reservation created for ${customer.display_name}: a pay link for ${formatMoney(
              created.amount_charged,
              created.currency,
            )} was sent.`,
      );
      onCreated();
      onOpenChange(false);
    } catch (error) {
      if (
        isApiError(error) &&
        error.status === 409 &&
        error.code === "STAFF_BOOKING_PLAYER_UNAVAILABLE"
      ) {
        setSubmitError(
          "That customer is no longer available. Search and choose another customer.",
        );
        setSelectedCustomer(null);
        form.setValue("customerQuery", "");
        setSettledCustomerQuery("");
      } else if (isApiError(error) && error.status === 409) {
        setSubmitError(
          "That court is no longer available. Choose another court or time.",
        );
        setAvailabilityRefreshRevision((current) => current + 1);
      } else if (isApiError(error) && error.status === 403) {
        setSubmitError(
          "You no longer have permission to create reservations at this facility.",
        );
      } else if (isApiError(error) && error.status === 404) {
        setSubmitError(
          "The selected customer, court, or facility is no longer available.",
        );
      } else if (isApiError(error) && error.status === 400) {
        setSubmitError(
          "Reservation details are no longer valid. Review the customer, court, party, and time.",
        );
      } else {
        setSubmitError("The reservation could not be created. Try again.");
      }
    } finally {
      submitInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        disableRestoreFocus
        aria-labelledby="create-reservation-dialog-title"
        aria-describedby="create-reservation-dialog-description"
        onClose={() => {
          if (!submitting) onOpenChange(false);
        }}
        slotProps={{
          paper: {
            sx: { maxHeight: "calc(100dvh - 2rem)" },
          },
          transition: {
            onExited: () => returnFocusRef.current?.focus(),
          },
        }}
      >
        <DialogTitle
          id="create-reservation-dialog-title"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            Create reservation
          </Box>
          <IconButton
            aria-label="Close reservation dialog"
            edge="end"
            size="small"
            disabled={submitting}
            onClick={() => {
              if (!submitting) onOpenChange(false);
            }}
          >
            <CloseRoundedIcon aria-hidden="true" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 1fr) minmax(0, 1fr)",
            },
            alignItems: "start",
            gap: { xs: 3, md: 4 },
          }}
        >
          <DialogContentText
            id="create-reservation-dialog-description"
            sx={{ gridColumn: "1 / -1", m: 0 }}
          >
            At {facilityName}, reserve one court for a known customer and review
            the server price.
          </DialogContentText>

          <Stack spacing={3} sx={{ minWidth: 0 }}>
            <Box
              component="section"
              aria-labelledby="reservation-customer-heading"
            >
              <Typography
                id="reservation-customer-heading"
                component="h3"
                variant="subtitle2"
                sx={{ mb: 1 }}
              >
                Customer
              </Typography>
              {selectedCustomer ? (
                <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
                    >
                      {selectedCustomer.display_name}
                    </Typography>
                    <Button
                      type="button"
                      variant="text"
                      size="small"
                      disabled={submitting}
                      onClick={() => {
                        setSelectedCustomer(null);
                        form.setValue("customerQuery", "");
                        setSubmitError(null);
                      }}
                    >
                      Change
                    </Button>
                  </Stack>
                </Paper>
              ) : (
                <Stack spacing={1}>
                  <TextField
                    id="reservation-customer-search"
                    type="search"
                    label="Search customer"
                    size="small"
                    fullWidth
                    placeholder="Search by name"
                    {...form.register("customerQuery", {
                      onChange: () => setSubmitError(null),
                    })}
                    value={customerQuery}
                    disabled={submitting}
                    slotProps={{
                      htmlInput: {
                        "aria-label": "Search customer",
                        "aria-busy": customerSearchStatus === "loading",
                      },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon
                              fontSize="small"
                              aria-hidden="true"
                            />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  {customerSearchStatus === "loading" ? (
                    <Stack
                      role="status"
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <CircularProgress size={14} />
                      <Typography color="text.secondary" variant="caption">
                        Searching customers
                      </Typography>
                    </Stack>
                  ) : null}
                  {customerSearchStatus === "error" ? (
                    <Alert severity="error">
                      Customer search failed. Check the name and try again.
                    </Alert>
                  ) : null}
                  {customerResults.length > 0 ? (
                    <List
                      aria-label="Customer results"
                      disablePadding
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      {customerResults.map((customer) => (
                        <ListItemButton
                          key={customer.user_id}
                          disabled={submitting}
                          onClick={() => {
                            setSelectedCustomer(customer);
                            form.setValue("customerQuery", "");
                            setSettledCustomerQuery("");
                            setSubmitError(null);
                          }}
                        >
                          <ListItemText
                            primary={customer.display_name}
                            slotProps={{
                              primary: {
                                variant: "body2",
                                sx: { overflowWrap: "anywhere" },
                              },
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  ) : null}
                </Stack>
              )}
            </Box>

            <Box component="section" aria-labelledby="reservation-time-heading">
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 1,
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  id="reservation-time-heading"
                  component="h3"
                  variant="subtitle2"
                >
                  Time
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  Facility time ({facilityTimeZone})
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                <TextField
                  id="reservation-start"
                  label="Start"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  {...form.register("startLocal", {
                    onChange: () => setSubmitError(null),
                  })}
                  value={startLocal}
                  disabled={submitting}
                  error={Boolean(startValidationIssue)}
                  helperText={startValidationIssue?.message}
                  slotProps={{
                    formHelperText: { role: "alert" },
                    htmlInput: { "aria-label": "Start time" },
                    inputLabel: { shrink: true },
                  }}
                />
                <TextField
                  id="reservation-end"
                  label="End"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  {...form.register("endLocal", {
                    onChange: () => setSubmitError(null),
                  })}
                  value={endLocal}
                  disabled={submitting}
                  error={Boolean(endValidationIssue)}
                  helperText={endValidationIssue?.message}
                  slotProps={{
                    formHelperText: { role: "alert" },
                    htmlInput: { "aria-label": "End time" },
                    inputLabel: { shrink: true },
                  }}
                />
              </Box>
            </Box>

            <Box
              component="section"
              aria-labelledby="reservation-party-heading"
            >
              <Typography
                id="reservation-party-heading"
                component="h3"
                variant="subtitle2"
                sx={{ mb: 1 }}
              >
                Party
              </Typography>
              {allocationEnabled ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    id="reservation-party-size"
                    label="Party size"
                    type="number"
                    size="small"
                    fullWidth
                    {...form.register("playerCountInput", {
                      onChange: () => setSubmitError(null),
                    })}
                    value={playerCountInput}
                    disabled={submitting}
                    error={Boolean(playerCountValidationIssue)}
                    helperText={playerCountValidationIssue?.message}
                    slotProps={{
                      formHelperText: { role: "alert" },
                      htmlInput: {
                        "aria-label": "Party size",
                        min: 1,
                        max: allocationCap,
                        step: 1,
                        inputMode: "numeric",
                      },
                    }}
                  />
                  <TextField
                    id="reservation-non-members"
                    label="Non-member players"
                    type="number"
                    size="small"
                    fullWidth
                    {...form.register("nonMemberCountInput", {
                      onChange: () => setSubmitError(null),
                    })}
                    value={nonMemberCountInput}
                    disabled={submitting}
                    error={Boolean(nonMemberCountValidationIssue)}
                    helperText={nonMemberCountValidationIssue?.message}
                    slotProps={{
                      formHelperText: { role: "alert" },
                      htmlInput: {
                        "aria-label": "Non-member players",
                        min: 0,
                        max: playerCountInput || allocationCap,
                        step: 1,
                        inputMode: "numeric",
                      },
                    }}
                  />
                </Box>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  This facility uses the standard one-player rate. Party
                  allocation is not enabled.
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack spacing={3} sx={{ minWidth: 0 }}>
            <Box
              component="section"
              aria-labelledby="reservation-court-heading"
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 1,
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  id="reservation-court-heading"
                  component="h3"
                  variant="subtitle2"
                >
                  Court
                </Typography>
                <Stack
                  role="status"
                  aria-live="polite"
                  direction="row"
                  spacing={0.75}
                  sx={{ alignItems: "center" }}
                >
                  {availabilityStatus === "loading" ? (
                    <CircularProgress size={14} />
                  ) : null}
                  <Typography color="text.secondary" variant="caption">
                    {availabilityStatus === "loading"
                      ? "Checking availability"
                      : availabilityStatus === "error"
                        ? "Availability failed"
                        : availabilityStatus === "ready"
                          ? "Availability checked"
                          : "Enter times to check"}
                  </Typography>
                </Stack>
              </Stack>
              {enabledCourts.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No enabled courts are available for reservations.
                </Typography>
              ) : (
                <RadioGroup
                  aria-label="Court"
                  tabIndex={0}
                  name="selectedCourtId"
                  value={selectedCourtIdValue}
                  onChange={(event) => {
                    form.setValue("selectedCourtId", event.target.value, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                    setSubmitError(null);
                  }}
                  sx={{
                    maxHeight: { xs: "none", md: 320 },
                    overflowY: "auto",
                    overflowX: "hidden",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  {enabledCourts.map((court) => {
                    const verdict = verdicts.get(court.court_id);
                    const statusId = `reservation-court-status-${court.court_id}`;
                    const disabled =
                      submitting ||
                      availabilityStatus !== "ready" ||
                      verdict?.available !== true;
                    const statusColor =
                      availabilityStatus === "ready" && verdict?.available
                        ? "success.main"
                        : availabilityStatus === "error" ||
                            verdict?.available === false
                          ? "error.main"
                          : "text.secondary";
                    return (
                      <Box
                        component="label"
                        key={court.court_id}
                        sx={{
                          display: "flex",
                          minHeight: 48,
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          cursor: disabled ? "default" : "pointer",
                          opacity: disabled ? 0.68 : 1,
                          "&:not(:last-of-type)": {
                            borderBottom: 1,
                            borderColor: "divider",
                          },
                          "&:hover": disabled
                            ? undefined
                            : { bgcolor: "action.hover" },
                        }}
                      >
                        <Radio
                          value={court.court_id}
                          checked={selectedCourtId === court.court_id}
                          disabled={disabled}
                          slotProps={{
                            input: {
                              "aria-label": court.name,
                              "aria-describedby": statusId,
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontWeight: 600,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {court.name}
                        </Typography>
                        <Typography
                          id={statusId}
                          color={statusColor}
                          variant="caption"
                          sx={{ textAlign: "right", maxWidth: "48%" }}
                        >
                          {availabilityText(availabilityStatus, verdict)}
                        </Typography>
                      </Box>
                    );
                  })}
                </RadioGroup>
              )}
            </Box>

            <Paper
              component="section"
              variant="outlined"
              aria-labelledby="reservation-price-heading"
              sx={{ p: 2 }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography
                  id="reservation-price-heading"
                  component="h3"
                  variant="subtitle2"
                >
                  Reservation total
                </Typography>
                {quoteStatus === "loading" ? (
                  <Stack
                    role="status"
                    direction="row"
                    spacing={0.75}
                    sx={{ alignItems: "center" }}
                  >
                    <CircularProgress size={14} />
                    <Typography color="text.secondary" variant="caption">
                      Loading price
                    </Typography>
                  </Stack>
                ) : quoteStatus === "ready" && quote ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "baseline" }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {formatMoney(asMoney(quote.total), quote.currency)}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {quote.currency.toUpperCase()}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    —
                  </Typography>
                )}
              </Stack>
              {quoteStatus === "ready" &&
              quote?.pricing_rule_matched === false ? (
                <Typography
                  color="warning.main"
                  variant="caption"
                  component="p"
                  sx={{ mt: 1 }}
                >
                  No reservation rate matches this time, so it is booked at no
                  charge.
                </Typography>
              ) : null}
              {noCharge ? null : (
              <RadioGroup
                aria-label="Payment"
                onChange={(event) =>
                  setPayment(event.target.value as StaffBookingPayment)
                }
                sx={{ mt: 1 }}
                value={payment}
              >
                <FormControlLabel
                  control={<Radio size="small" />}
                  label="Player pays (send pay link)"
                  value="pay_link"
                />
                {canWaivePayment ? (
                  <FormControlLabel
                    control={<Radio size="small" />}
                    label="Free"
                    value="free"
                  />
                ) : null}
              </RadioGroup>
              )}
            </Paper>

            {visibleError ? (
              <Alert severity="error">{visibleError}</Alert>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, flexShrink: 0 }}>
          <Button
            type="button"
            variant="outlined"
            size="small"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            size="small"
            disabled={!canSubmit}
            onClick={() => void form.handleSubmit(handleSubmit)()}
          >
            {submitting ? "Creating…" : "Create reservation"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={(_event, reason) => {
          if (reason !== "clickaway") setSuccessMessage(null);
        }}
      >
        {successMessage ? (
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
